/**
 * SmsService service is used to send sms request to jobs server.
 */
fx.service( class SmsService extends fx.Service {

    constructor(model)
    {
        super();
        this.model = model.creationModel;
        
        this._phoneNumber = undefined;
        this._qrCode = "";
        this._pending = false;
        this._startedFired = false;

        // keep polling after the phone signals "finished" so it can send another batch while the
        // screen stays open. Off by default : print/poster/calendar keep their stop-on-finished flow.
        this._continuous = false;
        this._finishedFired = false;

        // the photos received from the phone, kept apart so a screen can show them on their own
        // (SelectPhotosPanel). They are also added to the album below, as before.
        this._receivedPhotos = new fx.ModelArray();

        // ids already received, for O(1) dedup : status returns the full cumulative list every poll
        this._receivedIds = new Set();

        // how many photos this screen expects in all, and how many were already on it when the batch
        // being sent was announced — see _readTotal
        this._total = 0;
        this._batchBase = null;
    }

    // how many photos the phone said it would send, 0 until it has said anything
    get total()
    {
        return this._total;
    }

    // photos were announced that have not arrived yet : what "Importer" has to warn about, and what the
    // magic album's steps wait on
    get pending()
    {
        return this._pending;
    }

    // keep receiving after "finished" (a screen that lets the user add more before importing)
    get continuous()
    {
        return this._continuous;
    }

    set continuous(v)
    {
        this._continuous = !!v;
    }

    // the photos received from the phone in this session (a collection a screen can bind to)
    get receivedPhotos()
    {
        return this._receivedPhotos;
    }

    // fired by the Main on validation ; the photos are already in the album
    get importedEvent()
    {
        return "show-editor";
    }

    // wording of the "not finished yet" confirmation
    get confirmEarly()
    {
        return {
            title : fx.text("Envoi pas encore terminé"),
            text : fx.text("Vos photos arrivent encore depuis votre mobile. Importer seulement les $ photos déjà reçues ?", this._receivedPhotos.length)
        };
    }

    get qrCode()
    {
        return this._qrCode;
    }    

    get phoneNumber()
    {
        return this._phoneNumber;
    }    

    set phoneNumber(phoneNumber)
    {
        this._phoneNumber = phoneNumber;
    }    

    start(){
        this._startedFired = false;
        this._finishedFired = false;
        this._total = 0;
        this._batchBase = null;
        this._receivedIds.clear();
        this._receivedPhotos.removeAll(true);   // a fresh session : forget the previous photos
        fx.service("SmsService").generateQrCode();
        this.repeat(this._checkUploadStatus, 2000);   // poll often so the photos show up as they arrive
    }

    // stop polling for new photos when leaving the import screen (import, early import or quit). In
    // continuous mode polling no longer stops on its own at "finished", so the screen must stop it.
    // Note : this does not stop the phone itself — that needs a "cancel" action server-side + the
    // mobile uploader checking it between chunks (not built yet).
    stop(){
        this.repeat(this._checkUploadStatus, null);
        this._receivedIds.clear();   // leaving is paired with receivedPhotos.removeAll() : keep the dedup set in sync
        this._total = 0;
        this._batchBase = null;
    }

    generateQrCode(){
        this.smsAPI("get", null, this.model.creation.id, this._getQrCodeCallback);
    }

    send(phoneNumber, dialcode)
    {
        this.phoneNumber = phoneNumber;
        if (this.phoneNumber)
        {
            let url = "/app/uploader";
            this.post(
                url,
                {
                    action: "send",
                    phoneNumber: this._phoneNumber,
                    dialcode: dialcode,
                    bookId: this.model.creation.id
                }
            );
        }
    }

    _checkUploadStatus(){
        this.smsAPI("status", null, this.model.creation.id, this._checkUploadStatusCallback);
    }

    _getQrCodeCallback(data){
        this._qrCode = data.qrcode;
    }

    addPhotos(photos)
    {
        this.model.creation.photosort = 0;

        for(let i = 0; i < photos.length; i++ )
        {
            let data = photos.getItemAt(i);

            // status is polled repeatedly : only add a photo the first time it shows up (O(1) via the id set)
            if (this._receivedIds.has(data.id))
                continue;
            this._receivedIds.add(data.id);

            let photo = new models.Photo( data );

            // stage first, add to the album last : a Model keeps a single parentModel, so the last
            // addItem wins — the album must win so photo.delete() removes it from the album and bubbles
            this._receivedPhotos.addItem( photo );
            this.model.photos.addItem( photo );

            this.trigger("mobile-upload-photo", photo);
        }
    }

    _checkUploadStatusCallback(data)
    {
        if(data.started && !this._startedFired)
        {
            this._startedFired = true;
            this.model.event("mobile-upload-started", data);
        }

        this._readTotal(data);

        // add the photos received so far, as they arrive (addPhotos skips the ones already added)
        let before = this._receivedPhotos.length;
        if(data.photos)
            this.addPhotos(data.photos);
        let gotNew = this._receivedPhotos.length > before;

        // a new photo after a "finished" was announced means a fresh batch : re-arm so the next
        // finished re-announces done and the screen reopens its reception state
        if(gotNew && this._finishedFired)
            this._finishedFired = false;

        // measured against the total, not against `started` : that flag is raised once per send and
        // cleared as soon as it has been reported, so it was true for a single poll and pending was
        // false for the rest of an upload — which is when it actually matters. Computed here, after the
        // photos of this poll have been added, so both sides of the comparison are current.
        this._pending = !data.finished && this._total > this._receivedPhotos.length;

        if(data.finished)
        {
            if(this._continuous)
            {
                // keep polling (the app may send another batch) but announce "finished" once per
                // batch, on the FIRST finished=1 poll. The backend flips finished back to 0 once the
                // photos are drained, so waiting for a later "settle" poll would miss the event.
                if(!this._finishedFired)
                {
                    this._finishedFired = true;
                    this.model.event("mobile-upload-finished", data.photos);
                }
            }
            else
            {
                this.repeat(this._checkUploadStatus, null);   // upload done : stop polling
                this.model.event("mobile-upload-finished", data.photos);
            }
        }
    }

    /**
     * The total the import screen measures itself against.
     *
     * `nb_photos` is the size of the batch being sent, not a running count : the phone overwrites it on
     * every send, so two sends of ten read 10 and 10, never 20. The screen's total is therefore what was
     * already on it when this batch started, plus what the batch announces.
     *
     * `started` marks that moment, and only that moment : the phone raises it from its "reset" — sent on
     * every tap of its send button, not only when its page opens — and status() clears it as soon as it
     * has reported it once. So a 1 means a send is beginning, and every poll of that send reads 0.
     *
     * Until the first one of this session, nothing is announced : a figure left on the row by an earlier
     * send describes photos this screen never saw.
     */
    _readTotal(data)
    {
        // read before addPhotos, so this is the count the batch starts from
        if (data.started)
            this._batchBase = this._receivedPhotos.length;

        if (this._batchBase === null)
            return;

        let total = this._batchBase + (parseInt(data.nb_photos, 10) || 0);

        // the batch size can still be corrected downwards : the row may carry the previous send's
        // figure until the customer has picked this one
        if (total != this._total)
        {
            this._total = total;
            this.model.event("mobile-upload-total", total);
        }
    }

    smsAPI(action, token, bookId, callback){
        this.post("/app/uploader",
        {
            action: action,
            token : token,
            bookId: bookId,
        }, callback);
    }

});

